import React, { useMemo } from 'react'
import styled from 'styled-components'
import { colors } from '../../../../../../../assets/colors'
import { Button } from '../../../../../../../components/Button'
import useDownloadMessages from '../../../../../../shared/hooks/useDownloadMessages'
import Icon from '../../../../../../../components/Icon'
import DownloadConfirmationModal from './DownloadConfirmationModal'
import { parseFilename, sortFiles } from '../../../../../../../utils/fileUtils'

const Container = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`

const Header = styled.div`
  margin-bottom: 30px;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.darkGrey};
  margin: 0 0 10px 0;
`

const Description = styled.p`
  font-size: 14px;
  color: ${colors.mediumGrey};
  line-height: 1.5;
  margin: 0;
`

const ActionSection = styled.div`
  background-color: ${colors.white};
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 25px;
  border: 1px solid ${colors.lightGrey};
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`

const DisabledNotice = styled.div`
  background-color: ${colors.silver};
  border: 1px solid ${colors.lightGrey};
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`

const NoticeText = styled.span`
  font-size: 13px;
  color: ${colors.mediumGrey};
  font-style: italic;
`

const DownloadSection = styled.div`
  background-color: ${colors.white};
  border-radius: 10px;
  border: 1px solid ${colors.lightGrey};
  overflow: hidden;
`

const DownloadHeader = styled.div`
  padding: 20px 25px;
  border-bottom: 1px solid ${colors.lightGrey};
  background-color: ${colors.silver};
`

const DownloadTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.darkGrey};
`

const DownloadList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 0;

  /* Custom scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.silver};
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.mediumGrey};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.darkGrey};
  }
`

const DownloadItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 25px;
  border-bottom: 1px solid ${colors.silver};
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${colors.silver};
  }
`

const FileName = styled.span`
  font-size: 14px;
  color: ${colors.darkGrey};
  flex: 1;
  margin-right: 15px;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
`

const DownloadButton = styled.button`
  background: ${colors.darkBlue};
  color: ${colors.white};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 8px 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${colors.blue};
  }
  
  &:active {
    opacity: 0.8;
  }
`

const ClearSection = styled.div`
  padding: 20px 25px;
  border-top: 1px solid ${colors.lightGrey};
  background-color: ${colors.silver};
  text-align: center;
`

const DownloadMessagesContent: React.FC = () => {
  const {
    isLoading,
    downloadLinks,
    isQueueStarted,
    canRequestDownload,
    showDownloadConfirmation,
    startDownloadQueue,
    checkDownloadLinks,
    downloadFile,
    clearDownloadLinks,
    hideDownloadConfirmation
  } = useDownloadMessages()

  const sortedDownloadLinks = useMemo(() => {
    return [...downloadLinks].sort((a, b) => sortFiles(a.filename, b.filename))
  }, [downloadLinks])

  return (
    <>
      <Container>
        <Header>
          <Title>Download Saved Messages</Title>
          <Description>
            Download all your saved Kate messages as ZIP files. Start the download queue to begin preparing your files, 
            then check the status to see when they&apos;re ready for download.
          </Description>
        </Header>
        
        <ActionSection>
          {!canRequestDownload && (
            <DisabledNotice>
              <Icon name="info" fill={colors.mediumGrey} width="16px" />
              <NoticeText>
                You&apos;ve already requested a download today. Please wait 24 hours before requesting again.
              </NoticeText>
            </DisabledNotice>
          )}
          
          <ButtonsContainer>
            <Button 
              type="button" 
              onClick={startDownloadQueue} 
              isLoading={isLoading && !downloadLinks.length}
              disabled={!canRequestDownload}
              style={{ minWidth: '180px' }}
            >
              Start Download Queue
            </Button>
            
            <Button 
              type="button" 
              onClick={checkDownloadLinks} 
              isLoading={isLoading}
              style={{ 
                minWidth: '180px',
                background: colors.blue,
                color: colors.white
              }}
            >
              Check Download Status
            </Button>
          </ButtonsContainer>
        </ActionSection>

        {downloadLinks.length > 0 && (
          <DownloadSection>
            <DownloadHeader>
              <DownloadTitle>
                Available Downloads ({downloadLinks.length} files)
              </DownloadTitle>
            </DownloadHeader>
            
            <DownloadList>
              {sortedDownloadLinks.map((link) => (
                <DownloadItem key={link.filename}>
                  <FileName>{link.filename}</FileName>
                  <DownloadButton onClick={() => downloadFile(link.url, link.filename)}>
                    <Icon name="download" fill={colors.white} width="16px" />
                    Download
                  </DownloadButton>
                </DownloadItem>
              ))}
            </DownloadList>

            <ClearSection>
              <Button 
                type="button" 
                onClick={clearDownloadLinks}
                style={{ 
                  background: colors.lightGrey, 
                  color: colors.mediumGrey,
                  minWidth: '120px'
                }}
              >
                Clear List
              </Button>
            </ClearSection>
          </DownloadSection>
        )}
      </Container>

      <DownloadConfirmationModal
        show={showDownloadConfirmation}
        onClose={hideDownloadConfirmation}
      />
    </>
  )
}

export default DownloadMessagesContent 